import { default as Axios } from 'axios';
import { csrfToken } from '../../utils/csrfTokenUtils';
import { viewerSessionHeaders } from '../../utils/viewerSession';

const axios = Axios.create({
    baseURL: '/'
});

// Resolves once the user has re-authenticated after a session timeout.
const waitForSessionRestore = () =>
    new Promise((resolve) => {
        const handler = () => {
            window.removeEventListener("app:session-restored", handler);
            resolve();
        };
        window.addEventListener("app:session-restored", handler);
    });

const waitForPrivacyLink = () =>
    new Promise((resolve) => {
        const finish = (saved) => () => {
            window.removeEventListener("app:privacy-link-saved", onSaved);
            window.removeEventListener("app:privacy-link-cancelled", onCancelled);
            resolve(saved);
        };
        const onSaved = finish(true);
        const onCancelled = finish(false);
        window.addEventListener("app:privacy-link-saved", onSaved);
        window.addEventListener("app:privacy-link-cancelled", onCancelled);
    });

export const ensurePrivacyLink = async (userId) => {
    if (!userId) return true;
    const { data } = await axios.get(`/profiles/${userId}.json`, { headers: { Accept: 'application/json' } });
    if (data?.privacy_link) return true;
    window.dispatchEvent(new CustomEvent("app:privacy-link-required"));
    return waitForPrivacyLink();
};

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        if (status === 403 && error?.response?.data?.error === "impersonation_ended") {
            try {
                sessionStorage.setItem("impersonation:flash", error.response.data.message || "Impersonation has ended.");
            } catch (storageError) {}
            window.location.href = error.response.data.redirect_to || "/";
            return new Promise(() => {});
        }
        if (status === 422 && error?.response?.data?.error === "privacy_link_required") {
            window.dispatchEvent(new CustomEvent("app:privacy-link-required"));
            return waitForPrivacyLink().then((saved) => (saved ? axios.request(error.config) : Promise.reject(error)));
        }
        if (status === 401) {
            if (typeof window !== "undefined" && window.__REQUIRES_AUTH__ && !window.__SESSION_TIMEOUT_ACTIVE__) {
                window.__SESSION_TIMEOUT_ACTIVE__ = true;
                window.dispatchEvent(new CustomEvent("app:session-expired"));
            }
            // Hold the original request open; retry it once session is restored.
            return waitForSessionRestore().then(() => {
                error.config.headers['X-CSRF-Token'] = csrfToken();
                return axios.request(error.config);
            });
        }
        return Promise.reject(error);
    }
);

// Global request deduplication for networkService - prevents duplicate calls
// Using a synchronous lock to prevent race conditions
const pendingNetworkRequests = new Map();

export const networkService = async (url, method = 'GET', data = null) => {
    try {
        const normalizedUrl = url?.startsWith('/') ? url : `/${url}`;
        const normalizedMethod = (method || 'GET').toUpperCase();

        // Create a unique key for deduplication
        // For GET requests, use URL only
        // For POST/PUT/PATCH requests, use URL + method + payload
        let requestKey = normalizedUrl;
        if (normalizedMethod !== 'GET') {
            // Create a stable key from the payload (excluding authenticity_token which changes)
            const payloadForKey = { ...(data || {}) };
            // Sort keys for consistent serialization
            const sortedPayload = Object.keys(payloadForKey)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = payloadForKey[key];
                    return acc;
                }, {});
            const payloadString = JSON.stringify(sortedPayload);
            requestKey = `${normalizedUrl}::${normalizedMethod}::${payloadString}`;
        }

        // CRITICAL: Check if there's already a pending request with the same key
        if (pendingNetworkRequests.has(requestKey)) {
            // Return the pending promise instead of making a new request
            return pendingNetworkRequests.get(requestKey);
        }

        // Create the request promise
        const requestPromise = new Promise(async (resolve, reject) => {
            try {
                const normalizedData = data ? { ...data, 'authenticity_token': csrfToken() } : null;
                const headers = {
                    'X-CSRF-Token': csrfToken(),
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...viewerSessionHeaders()
                };

                let requestData = null;
                if (normalizedData) {
                    requestData = JSON.stringify(normalizedData);
                }

                const response = await axios({
                    url: normalizedUrl,
                    method: normalizedMethod,
                    headers: headers,
                    ...(requestData && { data: requestData }),
                });
                resolve(response);
            } catch (error) {
                reject(error);
            } finally {
                // Remove from pending requests AFTER completion
                pendingNetworkRequests.delete(requestKey);
            }
        });

        // Store the promise IMMEDIATELY (synchronously) before any async operations
        pendingNetworkRequests.set(requestKey, requestPromise);

        return requestPromise;
    } catch (error) {
        console.error('Error during network service:', error);
        throw error;
    }
};
